import { Component, HostBinding, Inject, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TuiDialog } from '@taiga-ui/cdk';
import { POLYMORPHEUS_CONTEXT } from '@tinkoff/ng-polymorpheus';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TuiCountryIsoCode } from '@taiga-ui/i18n';
import { ClientSubscriptionResponse } from '@fb-tool-front/shared/interfaces/auth.type';
import { AuthService } from '@fb-tool-front/core/services/auth.service';
import { ErrorService } from '@fb-tool-front/core/services/error.service';
import { TUI_CLOSE_WORD } from '@taiga-ui/core';
import { MetricsService, TRACKABLE_EVENTS } from '@fb-tool-front/core/services';
import { LocaleService } from '@fb-tool-front/core/services/locale.service';
import { LOCALES } from '@fb-tool-front/shared/constants/locale.constant';
import { ClientSubscriptionTypes } from '@fb-tool-front/shared/constants/auth.constants';
import { DomSanitizer } from '@angular/platform-browser';

@UntilDestroy()
@Component({
  selector: 'app-demo-request',
  templateUrl: './demo-request.component.html',
  styleUrls: ['./demo-request.component.scss']
})
export class DemoRequestComponent implements OnInit {
  public clientSubscription$: Observable<Partial<ClientSubscriptionResponse>>;
  private sendingSubscription$$ = new BehaviorSubject<boolean>(false);
  public sendingSubscription$ = this.sendingSubscription$$.asObservable();
  public readonly clientSubscriptionTypes = ClientSubscriptionTypes;
  public readonly LOCALES = LOCALES;
  public locale = this.localeService.locale;

  public requestForm: UntypedFormGroup = new UntypedFormGroup({});

  readonly countries = Object.values(TuiCountryIsoCode);

  countryIsoCode =
    this.localeService.locale === LOCALES.en ? TuiCountryIsoCode.US : TuiCountryIsoCode.RU;

  constructor(
    public authService: AuthService,
    @Inject(POLYMORPHEUS_CONTEXT) readonly context: TuiDialog<any, any>,
    @Inject(TUI_CLOSE_WORD) readonly closeWord$: Observable<string>,
    private errorService: ErrorService,
    private metricsService: MetricsService,
    private localeService: LocaleService,
    @Inject(DomSanitizer) private readonly sanitizer: DomSanitizer
  ) {}

  public close(res: boolean = false) {
    this.context.completeWith(res);
  }

  ngOnInit() {
    this.requestForm = new UntypedFormGroup({
      contact_name: new UntypedFormControl(null, Validators.required),
      contact_email: new UntypedFormControl(this.authService.user$$.value.email, [
        Validators.email,
        Validators.required
      ]),
      contact_phone: new UntypedFormControl(null, [Validators.required, Validators.minLength(12)]),
      contact_company: new UntypedFormControl(null, Validators.required),
      prefer_whats_app: new UntypedFormControl(false)
    });
    this.clientSubscription$ = this.authService
      .getClientSubscription(this.context.data.clientSubscription)
      .pipe(
        map(el => {
          const newEl: Partial<ClientSubscriptionResponse> = { ...el } || {};
          newEl.status = el?.status || 'waiting';

          return newEl;
        }),
        catchError(error => {
          this.errorService.captureError(error);

          const errorResult: Partial<ClientSubscriptionResponse> = { status: 'error' };

          return of(errorResult);
        })
      );
  }

  sendRequest() {
    this.sendingSubscription$$.next(true);

    this.authService
      .setClientSubscription({
        subscription: this.context.data.clientSubscription,
        ...this.requestForm.value
      })
      .pipe(
        untilDestroyed(this),
        tap(() => this.metricsService.trackEvent(TRACKABLE_EVENTS.submitRequestDemoModal())),
        finalize(() => this.sendingSubscription$$.next(false))
      )
      .subscribe(() => this.close(true));
  }
}
